import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FoxLogo } from '../components/FoxLogo';
import { ArrowLeft, Building, User } from 'lucide-react';
export function Login() {
  const [activeTab, setActiveTab] = useState<'customer' | 'business'>('customer');
  const navigate = useNavigate();
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeTab === 'customer') {
      navigate('/dashboard/customer');
    } else {
      navigate('/dashboard/business');
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden" data-id="element-1896">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-1897">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-fox-600/10 rounded-full blur-[100px]" data-id="element-1898" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-amber-500/10 rounded-full blur-[100px]" data-id="element-1899" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.02]" data-id="element-1900" />
      </div>

      <Link to="/" className="absolute top-8 left-8 text-slate-400 hover:text-white flex items-center gap-2 transition-colors z-10" data-id="element-1901">
        <ArrowLeft size={20} data-id="element-1902" />
        <span data-id="element-1903">Back to Home</span>
      </Link>

      <div className="w-full max-w-md relative z-10" data-id="element-1904">
        <div className="text-center mb-8" data-id="element-1905">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 20
        }} className="inline-block mb-4" data-id="element-1906">
            <FoxLogo className="w-16 h-16" data-id="element-1907" />
          </motion.div>
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1908">
            Welcome Back
          </h1>
          <p className="text-slate-400" data-id="element-1909">Log in to your Fox Line account</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl" data-id="element-1910">
          {/* Tabs */}
          <div className="flex relative border-b border-slate-800" data-id="element-1911">
            <button onClick={() => setActiveTab('customer')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors z-10 ${activeTab === 'customer' ? 'text-white' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-1912">
              <User size={18} data-id="element-1913" />
              Customer
            </button>
            <button onClick={() => setActiveTab('business')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors z-10 ${activeTab === 'business' ? 'text-white' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-1914">
              <Building size={18} data-id="element-1915" />
              Business
            </button>

            {/* Animated Tab Indicator */}
            <motion.div className="absolute bottom-0 left-0 h-0.5 bg-fox-500 w-1/2" animate={{
            x: activeTab === 'customer' ? 0 : '100%'
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-1916" />
          </div>

          {/* Form Content */}
          <div className="p-8" data-id="element-1917">
            <form onSubmit={handleLogin} className="space-y-5" data-id="element-1918">
              {activeTab === 'business' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="space-y-2" data-id="element-1919">
                  <label className="text-sm font-medium text-slate-300" data-id="element-1920">
                    Company ID
                  </label>
                  <input type="text" required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="e.g. FOX-12345" data-id="element-1921" />
                </motion.div>}

              <div className="space-y-2" data-id="element-1922">
                <label className="text-sm font-medium text-slate-300" data-id="element-1923">
                  {activeTab === 'business' ? 'Work Email' : 'Email Address'}
                </label>
                <input type="email" required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="you@example.com" data-id="element-1924" />
              </div>

              <div className="space-y-2" data-id="element-1925">
                <div className="flex items-center justify-between" data-id="element-1926">
                  <label className="text-sm font-medium text-slate-300" data-id="element-1927">
                    Password
                  </label>
                  <a href="#" className="text-xs text-fox-500 hover:text-fox-400" data-id="element-1928">
                    Forgot password?
                  </a>
                </div>
                <input type="password" required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="••••••••" data-id="element-1929" />
              </div>

              <div className="flex items-center" data-id="element-1930">
                <input type="checkbox" id="remember" className="w-4 h-4 rounded border-slate-700 bg-slate-950 text-fox-600 focus:ring-fox-500 focus:ring-offset-slate-900" data-id="element-1931" />
                <label htmlFor="remember" className="ml-2 text-sm text-slate-400" data-id="element-1932">
                  Remember me for 30 days
                </label>
              </div>

              <button type="submit" className="w-full bg-fox-600 hover:bg-fox-500 text-white font-medium py-3 rounded-lg transition-all shadow-[0_0_15px_rgba(234,88,12,0.2)] hover:shadow-[0_0_25px_rgba(234,88,12,0.4)] mt-2" data-id="element-1933">
                Sign In
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-400" data-id="element-1934">
              Don't have an account?{' '}
              <a href="#" className="text-fox-500 hover:text-fox-400 font-medium" data-id="element-1935">
                Sign up
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}